// Konami detector — keyboard + touch parity from a single sequence.
//
// One SEQ, one state machine, two input adapters: classifyKey for keyboard
// and classifyGesture for swipes/taps. Touch sequence mirrors ↑↑↓↓←→←→BA as
// 8 directional swipes + 2 taps — the documented mobile-web Konami convention
// (Konami-JS, MachinisteWeb/konami-code-js). Taps disambiguate to B/A by
// position in the sequence (a finger tap has no inherent letter identity).
//
// State machine is exported as createKonamiMatcher so tests can drive it
// without a DOM. bindKonami is the thin DOM-binding adapter that wires
// keydown + pointerdown/up/cancel/lostcapture into the matcher.

type DirSym = 'up' | 'down' | 'left' | 'right';
export type KeySym = DirSym | 'b' | 'a';
export type PointerSym = DirSym | 'tap';

const SEQ: readonly KeySym[] = ['up', 'up', 'down', 'down', 'left', 'right', 'left', 'right', 'b', 'a'];

const IDLE_RESET_MS = 4000;

// Default skip selector: any element a tap "means something else" for. Covers
// existing chips (.herm-chip--sound, .herm-chip--info, .herm-info-close,
// .herm-arcade-exit) and the new summary buttons via [data-action], plus any
// future links/inputs/contenteditable.
const DEFAULT_SKIP_TARGETS =
  'button, [role="button"], a[href], input, textarea, select, [contenteditable="true"]';

// Gesture classification thresholds. Tuned per
// .claude/research-results/2026-05-04-herminator-arcade-mobile-feedback-shader-blob.md
// (Q6) for iOS Safari 26.4 / 3× DPR / 120 Hz: swipe min ≈28 px on phones with
// a 44 px tablet cap, tap slop 14 px (accommodates fingertip jitter), swipe
// max 650 ms (8 swipes still complete in 2-4 s, but unintentional drags don't
// register), tap max 350 ms. The ignored gap between TAP_MAX_PX (14) and
// SWIPE_MIN_PX (≥28) is intentional: prevents borderline gestures from being
// mis-classified as either category.
const SWIPE_MAX_DURATION_MS = 650;
const SWIPE_MIN_PX_FLOOR = 28;
const SWIPE_MIN_PX_CAP = 44;
const SWIPE_MIN_VIEWPORT_RATIO = 0.07;
const TAP_MAX_PX = 14;
const TAP_MAX_DURATION_MS = 350;

// --- Pure state machine (no DOM, fully unit-testable) ----------------------

export interface KonamiMatcher {
  consume(sym: KeySym | null): void;
  consumePointer(sym: PointerSym | null): void;
  reset(): void;
  index(): number;
}

export interface MatcherOptions {
  idleResetMs?: number;
  now?: () => number; // injectable clock for tests
}

export function createKonamiMatcher(onMatch: () => void, opts: MatcherOptions = {}): KonamiMatcher {
  const idleResetMs = opts.idleResetMs ?? IDLE_RESET_MS;
  const now = opts.now ?? (() => performance.now());

  let i = 0;
  let lastInputAt = 0;

  const reset = (): void => {
    i = 0;
  };

  const consume = (sym: KeySym | null): void => {
    if (sym === null) return; // unrecognized input — no advance, no reset

    const t = now();
    if (i > 0 && t - lastInputAt > idleResetMs) i = 0;
    lastInputAt = t;

    const expected = SEQ[i];
    if (expected !== undefined && sym === expected) {
      i += 1;
      if (i === SEQ.length) {
        i = 0;
        onMatch();
      }
      return;
    }

    // Mismatch: restart from index 1 if the wrong sym happens to be SEQ[0],
    // mirroring the keyboard-only "user starts over mid-sequence" UX.
    i = sym === SEQ[0] ? 1 : 0;
  };

  const consumePointer = (sym: PointerSym | null): void => {
    if (sym === null) return;
    if (sym === 'tap') {
      const expected = SEQ[i];
      // Taps only count once the B/A tail is expected; earlier taps are noise.
      if (expected !== 'b' && expected !== 'a') return;
      consume(expected);
      return;
    }
    consume(sym);
  };

  return { consume, consumePointer, reset, index: () => i };
}

// --- Input adapters (pure functions; bindable in any environment) ---------

export function classifyKey(e: KeyboardEvent): KeySym | null {
  switch (e.key) {
    case 'ArrowUp':
      return 'up';
    case 'ArrowDown':
      return 'down';
    case 'ArrowLeft':
      return 'left';
    case 'ArrowRight':
      return 'right';
    default:
      break;
  }
  if (e.key === 'b' || e.key === 'B') return 'b';
  if (e.key === 'a' || e.key === 'A') return 'a';
  return null;
}

export function classifyGesture(dx: number, dy: number, durMs: number): PointerSym | null {
  const adx = Math.abs(dx);
  const ady = Math.abs(dy);

  if (adx < TAP_MAX_PX && ady < TAP_MAX_PX && durMs < TAP_MAX_DURATION_MS) return 'tap';
  if (durMs > SWIPE_MAX_DURATION_MS) return null;

  const viewportMin = typeof window !== 'undefined'
    ? Math.min(window.innerWidth, window.innerHeight)
    : 0;
  // max(28, min(44, 7% of minSide)) — floor on tiny screens, cap on tablets
  // so a swipe never has to travel an unreasonable distance regardless of
  // form factor.
  const minPx = Math.max(
    SWIPE_MIN_PX_FLOOR,
    Math.min(SWIPE_MIN_PX_CAP, SWIPE_MIN_VIEWPORT_RATIO * viewportMin),
  );

  const dominant = Math.max(adx, ady);
  if (dominant < minPx) return null;

  if (adx >= ady) return dx > 0 ? 'right' : 'left';
  return dy > 0 ? 'down' : 'up';
}

// --- Public binding: keyboard + pointer at document scope -----------------

export interface BindKonamiOptions {
  // Gates the pointer path only; keyboard is always live (mid-arcade arrow
  // presses are harmless — those keys do nothing in-game and arcade.start()
  // is a no-op while running).
  isBlocked?: () => boolean;
  // CSS selector of elements that should not classify their pointerdown as
  // the start of a swipe/tap. Default covers buttons, links, form fields,
  // and contenteditable surfaces.
  skipTargets?: string;
}

export function bindKonami(onMatch: () => void, opts: BindKonamiOptions = {}): () => void {
  const matcher = createKonamiMatcher(onMatch);
  const skipSelector = opts.skipTargets ?? DEFAULT_SKIP_TARGETS;

  // One in-flight pointer at a time. pointerup matches by id; mismatched
  // events are ignored.
  let active:
    | { id: number; x: number; y: number; t: number; target: Element }
    | null = null;

  const onKeyDown = (e: KeyboardEvent): void => {
    matcher.consume(classifyKey(e));
  };

  const onPointerDown = (e: PointerEvent): void => {
    if (opts.isBlocked?.()) return;
    if (e.isPrimary === false) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;

    if (e.target instanceof Element && e.target.closest(skipSelector)) return;

    const target = e.target instanceof Element ? e.target : document.documentElement;
    active = { id: e.pointerId, x: e.clientX, y: e.clientY, t: performance.now(), target };

    // setPointerCapture lives on Element, not document. Touch/pen only —
    // capturing a mouse pointer can confuse desktop click handlers. Wrap in
    // try/catch because the pointer may already be inactive by this turn.
    if (e.pointerType === 'touch' || e.pointerType === 'pen') {
      try {
        target.setPointerCapture(e.pointerId);
      } catch {
        /* pointer no longer active; pointerup/cancel handlers clean up */
      }
    }
  };

  const onPointerUp = (e: PointerEvent): void => {
    if (!active || e.pointerId !== active.id) return;
    const start = active;
    active = null;
    const dur = performance.now() - start.t;
    matcher.consumePointer(classifyGesture(e.clientX - start.x, e.clientY - start.y, dur));
  };

  const clearActive = (e?: Event): void => {
    if (!active) return;
    if (e instanceof PointerEvent && e.pointerId !== active.id) return;
    active = null;
  };

  document.addEventListener('keydown', onKeyDown);
  document.addEventListener('pointerdown', onPointerDown, { passive: true });
  document.addEventListener('pointerup', onPointerUp, { passive: true });
  document.addEventListener('pointercancel', clearActive, { passive: true });
  // lostpointercapture goes through capture phase — the spec says it fires on
  // the element that held capture, not necessarily through the document tree.
  document.addEventListener('lostpointercapture', clearActive as EventListener, true);

  return () => {
    document.removeEventListener('keydown', onKeyDown);
    document.removeEventListener('pointerdown', onPointerDown);
    document.removeEventListener('pointerup', onPointerUp);
    document.removeEventListener('pointercancel', clearActive);
    document.removeEventListener('lostpointercapture', clearActive as EventListener, true);
  };
}
